import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { IconX, IconMail, IconCheck, IconPhone } from '@tabler/icons-react';

import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import PageEmptyState from '@/components/EmptyState/PageEmptyState';
import SectionLoader from '@/components/Loader/SectionLoader';
import TextArea from '@/components/TextArea';
import {
  SupportStatusConfig,
  SupportUserTypeConfig,
} from '@/constants/constant';
import { ESupportStatus } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getSupportQueryById,
  updateSupportQueryStatus,
} from '@/service/supportQueryService';
import { ISupportQueryDetails } from '@/types';
import { ISupportUpdatePayload } from '@/types/payload';
import { getAssetUrl } from '@/utils';

import DiscardModal from './modals/DiscardModal';
import ImageModal from './modals/ImageModal';

const SupportDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatText } = useFormatText();
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const [data, setData] = useState<ISupportQueryDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [note, setNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null);
  const location = useLocation();

  const pageTitle = formatText('support.title');
  const reportId =
    location.state?.reportId ?? formatText('common.actions.view');

  useEffect(() => {
    setTitle(pageTitle);
    setIsBreadcrumb([
      { label: pageTitle, to: '/support-queries' },
      { label: reportId },
    ]);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle, reportId]);

  const fetchDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getSupportQueryById(id);
      if (response?.status) {
        setData(response?.data);
      }
    } catch (error) {
      console.error('Error fetching support query details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateQueryStatus = async (
    status: ESupportStatus,
    extraParams: Partial<ISupportUpdatePayload> = {}
  ) => {
    if (!id) return;

    setActionLoading(true);
    try {
      const payload: ISupportUpdatePayload = {
        status,
        ...(note && { note }),
        ...extraParams,
      };

      const response = await updateSupportQueryStatus(id, payload);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        if (status === ESupportStatus.DISCARD) setIsDiscardModalOpen(false);
        navigate('/support-queries');
      } else {
        console.error('Error response:', response?.message);
      }
    } catch (error) {
      console.error(`Error updating status to ${status}:`, error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = () => updateQueryStatus(ESupportStatus.RESOLVED);
  const handleDiscard = (reason: string) =>
    updateQueryStatus(ESupportStatus.DISCARD, { discard_reason: reason });

  if (loading) return <SectionLoader className="h-full" />;

  if (!data)
    return (
      <PageEmptyState
        image="/assets/svg/not-found.svg"
        title={formatText('common.no-data-found')}
        containerClassName="h-[70vh]"
      />
    );

  return (
    <div className="space-y-6 w-full">
      <div className="flex justify-between items-start flex-col lg:flex-row gap-4">
        <div className="flex flex-col gap-2">
          <div className="text-neutral-secondary font-medium flex items-center gap-2">
            {formatText('support.details.report-id')}: {data.report_id}{' '}
            {data.status !== ESupportStatus.PENDING && (
              <div
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  SupportStatusConfig[data.status as ESupportStatus]?.className
                }`}
              >
                {formatText(
                  SupportStatusConfig[data.status as ESupportStatus]?.label
                )}
              </div>
            )}
          </div>
          <h1 className="text-neutral-primary text-[28px] font-semibold leading-9 tracking-[-0.28px]">
            {data.issue}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {data.status === ESupportStatus.PENDING && (
            <>
              <Button
                variant="primary"
                onClick={handleResolve}
                loading={actionLoading}
                label={formatText('support.actions.resolve')}
                icon={<IconCheck size={20} />}
              />
              <Button
                variant="outline"
                label={formatText('support.actions.discard')}
                onClick={() => setIsDiscardModalOpen(true)}
                className="text-red-600! border-red-600! hover:bg-red-600! hover:text-white!"
                icon={<IconX size={20} />}
              />
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* User Info Card */}
        <div className="w-full lg:w-80.25 p-6 rounded-2xl border border-neutral-light-200 flex flex-col gap-10 bg-white h-fit">
          <div className="flex flex-col items-center gap-4 text-center">
            <AvatarGroupList
              list={[
                {
                  id: data.reported_by?.id,
                  name: data.reported_by?.name || '',
                  image:
                    getAssetUrl(data.reported_by?.asset) ||
                    data.reported_by?.profile_image,
                },
              ]}
              size={70}
              avatarClassName="w-[70px] h-[70px] text-xl"
            />
            <div className="text-neutral-primary text-2xl font-bold leading-8">
              {data.reported_by?.name}
            </div>
            {data.reported_by?.user_role && (
              <div
                className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  SupportUserTypeConfig[data?.reported_by?.user_role]?.className
                }`}
              >
                {formatText(
                  SupportUserTypeConfig[data?.reported_by?.user_role]?.label
                )}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="flex gap-2 items-start">
              <IconMail className="text-neutral-500 shrink-0" size={20} />
              <div className="flex flex-col justify-center gap-1 overflow-hidden">
                <div className="text-neutral-secondary text-sm leading-5">
                  {formatText('user-management.email')}
                </div>
                <div className="text-neutral-primary leading-6 break-all">
                  {data.reported_by?.email}
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-start">
              <IconPhone className="text-neutral-500 shrink-0" size={20} />
              <div className="flex flex-col justify-center gap-1">
                <div className="text-neutral-secondary text-sm leading-5">
                  {formatText('user-management.phone-number')}
                </div>
                <div className="text-neutral-primary leading-6">
                  {data.reported_by?.phone_code}{' '}
                  {data.reported_by?.phone_number}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 flex flex-col gap-10">
          <div className="flex flex-col gap-2">
            <div className="text-neutral-primary text-lg font-semibold leading-7">
              {formatText('support.details.description')}
            </div>
            <p className="text-neutral-primary text-sm leading-5 whitespace-pre-wrap p-4 bg-white border border-neutral-light-200 rounded-lg">
              {data?.description}
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="text-neutral-primary text-lg font-semibold leading-7 flex items-center gap-2">
              {formatText('support.details.evidence-attached')}
              {data?.assets && data.assets.length > 0 && (
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-light-50 text-indigo-secondary text-sm font-semibold">
                  {data?.assets?.length}
                </span>
              )}
            </div>
            {data?.assets && data?.assets?.length > 0 ? (
              <div className="flex flex-wrap gap-4">
                {data?.assets.map((asset, index) => (
                  <img
                    key={index}
                    src={asset}
                    alt="Evidence"
                    className="w-24 h-24 rounded-lg object-cover cursor-pointer border border-neutral-light-200 hover:opacity-80 transition-opacity"
                    onClick={() => setActiveImageIndex(index)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <IconX className="text-error-secondary" size={20} />
                <span className="text-error-secondary text-sm  leading-5">
                  {formatText('support.details.no-evidence')}
                </span>
              </div>
            )}
          </div>

          {(data?.note || data?.status === ESupportStatus.PENDING) && (
            <div className="flex flex-col gap-2">
              <label className="text-neutral-primary text-lg font-semibold leading-7">
                {formatText('support.details.note')}
              </label>
              {data.status === ESupportStatus.PENDING ? (
                <TextArea
                  name="note"
                  placeholder={formatText('support.details.note-placeholder')}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="shadow-none"
                  textAreaClassName="min-h-[86px]! text-sm  text-neutral-primary"
                  maxLength={500}
                />
              ) : (
                data.note && (
                  <p className="text-neutral-primary text-sm leading-5 whitespace-pre-wrap p-4 bg-white border border-neutral-light-200 rounded-lg">
                    {data?.note}
                  </p>
                )
              )}
            </div>
          )}

          {data?.discard_reason && (
            <div className="flex flex-col gap-2">
              <label className="text-neutral-primary text-lg font-semibold leading-7">
                {formatText('support.details.discard-reason')}
              </label>
              <p className="text-neutral-primary text-sm leading-5 whitespace-pre-wrap p-4 bg-white border border-neutral-light-200 rounded-lg">
                {' '}
                {data.discard_reason}
              </p>
            </div>
          )}
        </div>
      </div>

      {isDiscardModalOpen && (
        <DiscardModal
          setIsOpen={setIsDiscardModalOpen}
          onDiscard={handleDiscard}
          isLoading={actionLoading}
        />
      )}

      {activeImageIndex !== null && data?.assets && (
        <ImageModal
          activeImageIndex={activeImageIndex}
          setActiveImageIndex={setActiveImageIndex}
          assets={data.assets}
        />
      )}
    </div>
  );
};

export default SupportDetails;
